import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getFamilles, 
  createFamille, 
  getFamille,
  getCotisationsFamilles, 
  createPaiementFamille,
  createCotisationFamille,
  getMontantsMensuelsFamilles
} from '../services/api';
import { notificationService } from '../utils/notifications';
import WebcamCapture from '../components/WebcamCapture';
import Footer from '../components/Footer';
import './ConfirmationPaiements.css';

const DashboardFamille = () => {
  const navigate = useNavigate();
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('famille_user') || '{}');
    } catch {
      return {};
    }
  }, []);

  const [showFamilleForm, setShowFamilleForm] = useState(false);
  const [selectedFamille, setSelectedFamille] = useState(null);
  const [familles, setFamilles] = useState([]);
  const [familleData, setFamilleData] = useState(null);
  const [montantsMensuels, setMontantsMensuels] = useState([]);
  const [cotisationsParMois, setCotisationsParMois] = useState({});
  const [loadingCotisationsParMois, setLoadingCotisationsParMois] = useState({});
  const [moisExpande, setMoisExpande] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [tousLesMois, setTousLesMois] = useState([]);

  // État du formulaire famille
  const [familleForm, setFamilleForm] = useState({
    chef_famille_nom: '',
    nom_famille: '',
    adresse: '',
    quartier: '',
    commune: '',
    telephone: '',
    observations: '',
    statut: 'active'
  });

  const [photoChef, setPhotoChef] = useState(null);

  useEffect(() => {
    if (!user.id) {
      navigate('/login-famille');
      return;
    }
    loadFamilles();
    loadMontantsMensuels();
  }, [user, navigate]);

  const loadFamilles = async () => {
    try {
      const response = await getFamilles();
      setFamilles(response.data || []);
    } catch (err) {
      console.error('Erreur lors du chargement des familles:', err);
      setError('Erreur lors du chargement des familles');
    }
  };

  const loadMontantsMensuels = async () => {
    try {
      const response = await getMontantsMensuelsFamilles();
      setMontantsMensuels(response.data || []);
    } catch (err) {
      console.error('Erreur lors du chargement des montants:', err);
    }
  };

  const loadData = useCallback(async (familleId) => {
    try {
      setLoading(true);
      console.log('📊 Chargement des données pour la famille:', familleId);
      
      const [familleRes, montantsRes, cotisationsRes] = await Promise.allSettled([
        getFamille(parseInt(familleId)),
        getMontantsMensuelsFamilles(),
        getCotisationsFamilles({ famille_id: parseInt(familleId) })
      ]);
      
      // Traiter la réponse de la famille
      let familleDataResult = null;
      if (familleRes.status === 'fulfilled') {
        familleDataResult = familleRes.value.data;
      } else {
        console.error('❌ Erreur lors du chargement de la famille:', familleRes.reason);
        notificationService.error('Erreur', 'Impossible de charger les informations de la famille');
        return;
      }
      
      if (!familleDataResult) {
        console.error('❌ Famille non trouvée:', familleId);
        notificationService.error('Erreur', 'Famille non trouvée');
        return;
      }
      
      setFamilleData(familleDataResult);
      
      // Traiter la réponse des montants mensuels
      let montants = [];
      if (montantsRes.status === 'fulfilled') {
        montants = montantsRes.value.data || [];
      } else {
        console.warn('⚠️ Erreur lors du chargement des montants mensuels:', montantsRes.reason);
      }
      setMontantsMensuels(montants);
      
      // Traiter la réponse des cotisations
      let cotisations = [];
      if (cotisationsRes.status === 'fulfilled') {
        const responseData = cotisationsRes.value.data;
        cotisations = Array.isArray(responseData) ? responseData : (responseData?.data || []);
        console.log('📋 Cotisations trouvées:', cotisations.length);
      } else {
        console.warn('⚠️ Erreur lors du chargement des cotisations:', cotisationsRes.reason);
        cotisations = [];
      }
      
      const moisUniques = new Map();
      
      // D'abord, ajouter tous les mois qui ont des cotisations
      cotisations.forEach(cotisation => {
        const key = `${cotisation.annee}-${cotisation.mois}`;
        if (!moisUniques.has(key)) {
          moisUniques.set(key, {
            annee: cotisation.annee,
            mois: cotisation.mois,
            cotisations: []
          });
        }
        moisUniques.get(key).cotisations.push(cotisation);
      });
      
      // Ensuite, ajouter TOUS les mois qui ont un montant mensuel fixé
      montants.forEach(montant => {
        const key = `${montant.annee}-${montant.mois}`;
        if (!moisUniques.has(key)) {
          moisUniques.set(key, {
            annee: montant.annee,
            mois: montant.mois,
            cotisations: []
          });
        }
      });
      
      // Trier (plus récent en premier)
      let moisList = Array.from(moisUniques.values());
      moisList.sort((a, b) => {
        if (a.annee !== b.annee) return b.annee - a.annee;
        return b.mois - a.mois;
      });
      
      console.log('📅 Mois uniques trouvés:', moisList.length);
      setTousLesMois(moisList);
      
      // Grouper les cotisations par mois pour l'affichage
      const cotisationsGrouped = {};
      cotisations.forEach(cotisation => {
        const key = `${cotisation.annee}-${cotisation.mois}`;
        if (!cotisationsGrouped[key]) {
          cotisationsGrouped[key] = [];
        }
        cotisationsGrouped[key].push(cotisation);
      });
      setCotisationsParMois(cotisationsGrouped);
      
      console.log('✅ Données chargées avec succès');
    } catch (err) {
      console.error('❌ Erreur lors du chargement des données:', err);
      notificationService.error('Erreur', 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedFamille) {
      // Réinitialiser les états liés aux cotisations quand la famille change
      setCotisationsParMois({});
      setMoisExpande({});
      setLoadingCotisationsParMois({});
      setTousLesMois([]);
      setFamilleData(null);
      
      loadData(selectedFamille.id);
    }
  }, [selectedFamille, loadData]);

  const getMoisNom = (mois) => {
    const moisNoms = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    return moisNoms[mois - 1] || mois;
  };

  const loadCotisationsImpayeesParMois = async (mois, annee) => {
    if (!selectedFamille) return;
    
    const key = `${annee}-${mois}`;
    setLoadingCotisationsParMois(prev => ({ ...prev, [key]: true }));
    try {
      const response = await getCotisationsFamilles({ 
        famille_id: parseInt(selectedFamille.id),
        mois: mois,
        annee: annee
      });
      const responseData = response.data;
      const cotisations = Array.isArray(responseData) ? responseData : (responseData?.data || []);
      const cotisationsImpayees = cotisations.filter(c => c.statut === 'impaye');
      setCotisationsParMois(prev => ({ ...prev, [key]: cotisationsImpayees }));
    } catch (err) {
      console.error(`Erreur lors du chargement des cotisations pour ${mois}/${annee}:`, err);
      setCotisationsParMois(prev => ({ ...prev, [key]: [] }));
    } finally {
      setLoadingCotisationsParMois(prev => ({ ...prev, [key]: false }));
    }
  };

  const toggleMoisExpande = (mois, annee) => {
    const key = `${annee}-${mois}`;
    const isExpanded = moisExpande[key] || false;
    
    if (!isExpanded) {
      if (!cotisationsParMois[key]) {
        loadCotisationsImpayeesParMois(mois, annee);
      }
    }
    
    setMoisExpande(prev => ({ ...prev, [key]: !isExpanded }));
  };

  const handleConfirmerPaiement = async (mois, annee) => {
    if (!selectedFamille) {
      notificationService.error('Erreur', 'Impossible d\'identifier la famille.');
      return;
    }

    const familleIdNum = parseInt(selectedFamille.id);
    if (!familleIdNum) {
      notificationService.error('Erreur', 'Impossible d\'identifier la famille.');
      return;
    }

    // Trouver le montant mensuel pour ce mois
    const montantMensuel = montantsMensuels.find(m => m.mois === mois && m.annee === annee);
    if (!montantMensuel) {
      notificationService.error('Erreur', 'Aucun montant mensuel trouvé pour ce mois.');
      return;
    }

    const montant = parseFloat(montantMensuel.montant_mensuel || 0);
    if (!montant) {
      notificationService.error('Erreur', 'Aucun montant défini pour ce mois');
      return;
    }

    // Vérifier si une cotisation existe déjà pour ce mois
    const cotisationsExistantes = cotisationsParMois[`${annee}-${mois}`] || [];
    const cotisationExistante = cotisationsExistantes.find(c => c.statut === 'impaye');
    
    if (cotisationExistante && cotisationExistante.statut === 'paye') {
      notificationService.warning('Déjà payée', 'Cette cotisation est déjà payée.');
      return;
    }

    const familleNom = familleData ? familleData.nom_famille : selectedFamille.nom_famille || 'Famille';
    const messageConfirmation = `Confirmer le paiement de ${montant.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF pour ${familleNom} - ${getMoisNom(mois)} ${annee} ?`;

    if (!window.confirm(messageConfirmation)) {
      return;
    }

    try {
      setLoading(true);
      let cotisationId = cotisationExistante?.id;
      
      // Si la cotisation n'existe pas encore (pas d'ID), la créer d'abord
      if (!cotisationId) {
        const dateEcheance = new Date(annee, mois, 0);
        
        try {
          const cotisationResponse = await createCotisationFamille({
            famille_id: familleIdNum,
            mois: mois,
            annee: annee,
            montant: montant,
            date_echeance: dateEcheance.toISOString().split('T')[0]
          });
          
          if (cotisationResponse.data && cotisationResponse.data.cotisation) {
            cotisationId = cotisationResponse.data.cotisation.id;
          } else if (cotisationResponse.data && cotisationResponse.data.id) {
            cotisationId = cotisationResponse.data.id;
          } else {
            throw new Error('Structure de réponse inattendue lors de la création de la cotisation');
          }
        } catch (cotisationError) {
          console.error('❌ Erreur lors de la création de la cotisation:', cotisationError);
          const cotisationErrorMessage = cotisationError.response?.data?.message || cotisationError.message || 'Erreur lors de la création de la cotisation';
          notificationService.error('Erreur', cotisationErrorMessage);
          return;
        }
      }

      const paiementData = {
        famille_id: familleIdNum,
        montant: montant,
        date_paiement: new Date().toISOString().split('T')[0],
        type_paiement: 'cotisation_famille',
        cotisation_ids: [cotisationId]
      };

      try {
        await createPaiementFamille(paiementData);
        notificationService.success('Paiement confirmé', `Le paiement de ${familleNom} a été confirmé avec succès.`);
        
        // Recharger les cotisations pour ce mois
        await loadCotisationsImpayeesParMois(mois, annee);
        // Recharger les données de la famille
        await loadData(familleIdNum);
      } catch (paiementError) {
        console.error('❌ Erreur lors de la création du paiement:', paiementError);
        const paiementErrorMessage = paiementError.response?.data?.message || paiementError.response?.data?.error || paiementError.message || 'Erreur lors de la création du paiement';
        notificationService.error('Erreur', paiementErrorMessage);
      }
    } catch (err) {
      console.error('❌ Erreur générale lors de la confirmation du paiement:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Erreur lors de la confirmation du paiement';
      notificationService.error('Erreur', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChefCapture = (file, fieldName) => {
    setPhotoChef(file);
  };

  const handleFamilleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      Object.keys(familleForm).forEach(key => {
        formData.append(key, familleForm[key]);
      });
      if (photoChef) {
        formData.append('photo_chef', photoChef);
      }

      await createFamille(formData);
      notificationService.success('Famille enregistrée', 'La famille a été enregistrée avec succès !');
      setShowFamilleForm(false);
      setFamilleForm({
        chef_famille_nom: '',
        nom_famille: '',
        adresse: '',
        quartier: '',
        commune: '',
        telephone: '',
        observations: '',
        statut: 'active'
      });
      setPhotoChef(null);
      loadFamilles();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'enregistrement de la famille');
      notificationService.error('Erreur', err.response?.data?.message || 'Erreur lors de l\'enregistrement de la famille');
    } finally {
      setLoading(false);
    }
  };

  const handleFamilleSelect = (familleId) => {
    const famille = familles.find(f => f.id === parseInt(familleId));
    setSelectedFamille(famille);
    setShowFamilleForm(false);
  };

  const filteredFamilles = familles.filter(famille => {
    const search = searchTerm.toLowerCase();
    return (
      famille.numero_famille?.toLowerCase().includes(search) ||
      famille.nom_famille?.toLowerCase().includes(search) ||
      famille.chef_famille_nom?.toLowerCase().includes(search) ||
      famille.commune?.toLowerCase().includes(search)
    );
  });

  const handleLogout = () => {
    localStorage.removeItem('famille_token');
    localStorage.removeItem('famille_user');
    navigate('/login-famille');
  };

  if (loading && selectedFamille && !familleData) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  const famille = familleData || selectedFamille;

  return (
    <div className="App">
      <div className="header">
        <div className="header-content">
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <img src="/logo.png" alt="Logo" style={{ height: '50px', width: '50px', objectFit: 'contain' }} />
            <div>
              <h1>MUTU NDE ESPOIR ASBL</h1>
              <p>Gestion Familles & Cotisations</p>
              {famille && (
                <p style={{ fontSize: '14px', color: '#666' }}>
                  {famille.nom_famille} - Numéro: {famille.numero_famille}
                </p>
              )}
              {!famille && (
                <p style={{ fontSize: '14px', color: '#666' }}>
                  Connecté en tant que: {user.nom_complet || user.username}
                </p>
              )}
            </div>
          </div>
          <div className="header-actions">
            {selectedFamille ? (
              <button className="btn btn-secondary" onClick={() => {
                setSelectedFamille(null);
                setFamilleData(null);
                setCotisationsParMois({});
                setMoisExpande({});
                setTousLesMois([]);
              }}>
                ← Retour à la liste
              </button>
            ) : (
              <button 
                className="btn btn-primary"
                onClick={() => {
                  setShowFamilleForm(!showFamilleForm);
                  setSelectedFamille(null);
                }}
                style={{ marginRight: '10px' }}
              >
                {showFamilleForm ? '❌ Annuler' : '➕ Nouvelle famille'}
              </button>
            )}
            <button className="btn btn-danger" onClick={handleLogout}>
              Déconnexion
            </button>
          </div>
        </div>
      </div>

      <div className="container">
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {showFamilleForm && !selectedFamille && (
          <div className="card" style={{ marginBottom: '20px' }}>
            <h3>Nouvelle famille</h3>
            <form onSubmit={handleFamilleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Chef de famille *</label>
                  <input
                    type="text"
                    value={familleForm.chef_famille_nom}
                    onChange={(e) => setFamilleForm({ ...familleForm, chef_famille_nom: e.target.value })}
                    required
                    placeholder="Nom complet du chef de famille"
                  />
                </div>
                <div className="form-group">
                  <label>Nom de la famille *</label>
                  <input
                    type="text"
                    value={familleForm.nom_famille}
                    onChange={(e) => setFamilleForm({ ...familleForm, nom_famille: e.target.value })}
                    required
                    placeholder="Nom de la famille"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Adresse</label>
                  <input
                    type="text"
                    value={familleForm.adresse}
                    onChange={(e) => setFamilleForm({ ...familleForm, adresse: e.target.value })}
                    placeholder="Adresse complète"
                  />
                </div>
                <div className="form-group">
                  <label>Quartier</label>
                  <input
                    type="text"
                    value={familleForm.quartier}
                    onChange={(e) => setFamilleForm({ ...familleForm, quartier: e.target.value })}
                    placeholder="Quartier"
                  />
                </div>
                <div className="form-group">
                  <label>Commune</label>
                  <select
                    value={familleForm.commune}
                    onChange={(e) => setFamilleForm({ ...familleForm, commune: e.target.value })}
                  >
                    <option value="">Sélectionner une commune</option>
                    <option value="MAKISO">MAKISO</option>
                    <option value="KABONDO">KABONDO</option>
                    <option value="MANGOBO">MANGOBO</option>
                    <option value="LUBUNGA">LUBUNGA</option>
                    <option value="KISANGANI">KISANGANI</option>
                    <option value="TSHOPO">TSHOPO</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Téléphone</label>
                  <input
                    type="text"
                    value={familleForm.telephone}
                    onChange={(e) => setFamilleForm({ ...familleForm, telephone: e.target.value })}
                    placeholder="Numéro de téléphone"
                  />
                </div>
                <div className="form-group" style={{ flex: '1' }}>
                  <label>Observations</label>
                  <textarea
                    value={familleForm.observations}
                    onChange={(e) => setFamilleForm({ ...familleForm, observations: e.target.value })}
                    rows="3"
                    placeholder="Notes et observations"
                  />
                </div>
              </div>
              <div className="form-group">
                <WebcamCapture
                  onCapture={handlePhotoChefCapture}
                  label="Photo du chef de famille"
                  fieldName="photo_chef"
                  photoType="passeport"
                />
              </div>
              <div>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Enregistrement...' : '💾 Enregistrer'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowFamilleForm(false);
                    setFamilleForm({
                      chef_famille_nom: '',
                      nom_famille: '',
                      adresse: '',
                      quartier: '',
                      commune: '',
                      telephone: '',
                      observations: '',
                      statut: 'active'
                    });
                    setPhotoChef(null);
                  }}
                  style={{ marginLeft: '10px' }}
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        {selectedFamille && famille ? (
          <>
            {/* Carte d'information de la famille */}
            <div className="card" style={{ 
              marginBottom: '30px', 
              backgroundColor: '#e3f2fd', 
              border: '2px solid #2196f3',
              borderRadius: '10px',
              padding: '20px'
            }}>
              <h2 style={{ 
                marginBottom: '15px', 
                color: '#1565c0',
                fontSize: '22px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                👨‍👩‍👧‍👦 Informations de la famille
              </h2>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                gap: '15px' 
              }}>
                <div style={{
                  padding: '15px',
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #90caf9'
                }}>
                  <div style={{ fontSize: '12px', color: '#1565c0', marginBottom: '5px', fontWeight: 'bold' }}>NOM DE LA FAMILLE</div>
                  <div style={{ fontSize: '18px', color: '#1565c0', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    {famille.nom_famille || '-'}
                  </div>
                </div>
                <div style={{
                  padding: '15px',
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #90caf9'
                }}>
                  <div style={{ fontSize: '12px', color: '#1565c0', marginBottom: '5px', fontWeight: 'bold' }}>NUMÉRO FAMILLE</div>
                  <div style={{ fontSize: '18px', color: '#1565c0', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    {famille.numero_famille || '-'}
                  </div>
                </div>
                <div style={{
                  padding: '15px',
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #90caf9'
                }}>
                  <div style={{ fontSize: '12px', color: '#1565c0', marginBottom: '5px', fontWeight: 'bold' }}>CHEF DE FAMILLE</div>
                  <div style={{ fontSize: '16px', color: '#1565c0', whiteSpace: 'nowrap' }}>
                    {famille.chef_famille_nom || '-'}
                  </div>
                </div>
                {famille.commune && (
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    border: '1px solid #90caf9'
                  }}>
                    <div style={{ fontSize: '12px', color: '#1565c0', marginBottom: '5px', fontWeight: 'bold' }}>COMMUNE</div>
                    <div style={{ fontSize: '16px', color: '#1565c0', whiteSpace: 'nowrap' }}>
                      {famille.commune}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="card" style={{ marginBottom: '30px' }}>
              <h2 style={{ marginBottom: '20px' }}>📋 Confirmation des paiements par mois</h2>
              <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
                Confirmez le paiement mensuel pour chaque mois. Les familles paient le montant mensuel total fixé.
              </p>
              
              {tousLesMois.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  <p style={{ fontSize: '16px', marginBottom: '10px' }}>Aucune cotisation trouvée</p>
                  <p style={{ fontSize: '14px' }}>Cette famille n'a pas encore de cotisations enregistrées.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {tousLesMois.map((moisData) => {
                    const key = `${moisData.annee}-${moisData.mois}`;
                    const isExpanded = moisExpande[key] || false;
                    
                    // Obtenir les cotisations pour ce mois (impayées uniquement)
                    let cotisationsFamille = cotisationsParMois[key] || [];
                    // Si pas encore chargées, utiliser les cotisations du moisData
                    if (cotisationsFamille.length === 0 && moisData.cotisations) {
                      cotisationsFamille = moisData.cotisations.filter(c => c.statut === 'impaye');
                    }
                    
                    const loading = loadingCotisationsParMois[key] || false;
                    const totalImpaye = cotisationsFamille.reduce((sum, c) => sum + parseFloat(c.montant || 0), 0);
                    
                    // Trouver le montant mensuel fixé pour ce mois (si disponible)
                    const montantMensuel = montantsMensuels.find(m => m.mois === moisData.mois && m.annee === moisData.annee);
                    
                    return (
                      <div
                        key={key}
                        style={{
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          backgroundColor: '#fff'
                        }}
                      >
                        <div
                          style={{
                            padding: '15px',
                            backgroundColor: cotisationsFamille.length > 0 ? '#fff3cd' : '#d4edda',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            transition: 'background-color 0.2s'
                          }}
                          onClick={() => toggleMoisExpande(moisData.mois, moisData.annee)}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = cotisationsFamille.length > 0 ? '#ffe69c' : '#c3e6cb'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = cotisationsFamille.length > 0 ? '#fff3cd' : '#d4edda'}
                        >
                          <div style={{ flex: 1 }}>
                            <h3 style={{ margin: 0, fontSize: '18px', color: '#2c3e50' }}>
                              {getMoisNom(moisData.mois)} {moisData.annee}
                            </h3>
                            {montantMensuel ? (
                              <div style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
                                Montant mensuel: <strong style={{ fontSize: '16px', color: '#1565c0' }}>{parseFloat(montantMensuel.montant_mensuel).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF</strong>
                              </div>
                            ) : (
                              <div style={{ fontSize: '14px', color: '#999', marginTop: '5px', fontStyle: 'italic' }}>
                                Montant mensuel non fixé par l'administrateur
                              </div>
                            )}
                            {isExpanded && (
                              <div style={{ fontSize: '13px', color: '#666', marginTop: '8px' }}>
                                {cotisationsFamille.length > 0 ? (
                                  <span style={{ color: '#856404', fontWeight: 'bold' }}>
                                    Cotisation impayée • Montant: {totalImpaye.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF
                                  </span>
                                ) : (
                                  <span style={{ color: '#155724', fontWeight: 'bold' }}>
                                    ✓ Cotisation payée
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <div style={{ fontSize: '20px', color: '#666' }}>
                            {isExpanded ? '▼' : '▶'}
                          </div>
                        </div>
                        
                        {isExpanded && (
                          <div style={{ padding: '15px', backgroundColor: '#f8f9fa' }}>
                            {loading ? (
                              <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                                Chargement...
                              </div>
                            ) : (() => {
                              // Trouver le montant mensuel pour ce mois
                              const montantMensuel = montantsMensuels.find(m => m.mois === moisData.mois && m.annee === moisData.annee);
                              
                              if (!montantMensuel) {
                                return (
                                  <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                                    <strong>Aucun montant mensuel fixé pour ce mois</strong>
                                    <div style={{ fontSize: '13px', marginTop: '10px', color: '#999' }}>
                                      L'administrateur n'a pas encore fixé de montant mensuel pour ce mois.
                                    </div>
                                  </div>
                                );
                              }
                              
                              // Obtenir les cotisations pour ce mois
                              const toutesCotisations = moisData.cotisations || [];
                              const cotisationMois = toutesCotisations.find(c => c.mois === moisData.mois && c.annee === moisData.annee);
                              const montantTotal = parseFloat(montantMensuel.montant_mensuel || 0);
                              const estPayee = cotisationMois && cotisationMois.statut === 'paye';
                              
                              return (
                                <div style={{ textAlign: 'center', padding: '20px' }}>
                                  <div style={{ 
                                    marginBottom: '20px',
                                    padding: '20px',
                                    backgroundColor: estPayee ? '#d4edda' : '#fff3cd',
                                    borderRadius: '8px',
                                    border: `2px solid ${estPayee ? '#28a745' : '#ffc107'}`
                                  }}>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#2c3e50', marginBottom: '10px' }}>
                                      Montant mensuel à payer
                                    </div>
                                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: estPayee ? '#28a745' : '#856404', marginBottom: '10px' }}>
                                      {montantTotal.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF
                                    </div>
                                    <div style={{ fontSize: '14px', color: '#666' }}>
                                      {estPayee ? (
                                        <span style={{ color: '#28a745', fontWeight: 'bold' }}>
                                          ✓ Cotisation payée le {cotisationMois.date_paiement ? new Date(cotisationMois.date_paiement).toLocaleDateString('fr-FR') : '-'}
                                        </span>
                                      ) : (
                                        <span style={{ color: '#856404', fontWeight: 'bold' }}>
                                          ● Cotisation impayée
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {!estPayee && (
                                    <button
                                      className="btn btn-success"
                                      onClick={() => handleConfirmerPaiement(moisData.mois, moisData.annee)}
                                      disabled={loading}
                                      style={{ 
                                        fontSize: '16px', 
                                        padding: '12px 30px',
                                        fontWeight: 'bold'
                                      }}
                                    >
                                      ✓ Confirmer le paiement mensuel
                                    </button>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="card">
            <h2>Liste des familles ({filteredFamilles.length})</h2>
            <div className="form-group" style={{ marginBottom: '20px' }}>
              <input
                type="text"
                placeholder="🔍 Rechercher une famille..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: '100%', padding: '10px', border: '2px solid #ddd', borderRadius: '5px' }}
              />
            </div>
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr>
                    <th>Numéro</th>
                    <th>Nom famille</th>
                    <th>Chef de famille</th>
                    <th>Commune</th>
                    <th>Téléphone</th>
                    <th>Membres</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFamilles.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                        Aucune famille trouvée
                      </td>
                    </tr>
                  ) : (
                    filteredFamilles.map(famille => (
                      <tr key={famille.id}>
                        <td>{famille.numero_famille}</td>
                        <td>{famille.nom_famille}</td>
                        <td>{famille.chef_famille_nom}</td>
                        <td>{famille.commune}</td>
                        <td>{famille.telephone}</td>
                        <td>{famille.nombre_membres || 0}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleFamilleSelect(famille.id)}
                          >
                            💳 Payer
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default DashboardFamille;
